// Document -> neutral content model shared by the Word, plain-text and iCalendar exporters, so
// that every output format groups speakers, numbers entries and labels unfinished clips the same way.
import type { Clip, DocumentFull, InspectionMeta } from '../../../shared/types';
import { formatDuration, formatLongDate, formatTime, speakerName } from '../lib/format';

/** Shown instead of text while a clip is queued or being transcribed. */
export const PENDING_LABEL = '[prepis v teku]';
/** Shown instead of text when transcription failed permanently. */
export const ERROR_LABEL = '[prepis ni uspel]';
/** Shown for a finished clip that produced no words (e.g. silence). */
export const EMPTY_LABEL = '[brez besedila]';
/** Added to the previous text of a clip that is being transcribed again (the text may still change). */
export const RETRANSCRIBING_LABEL = '[ponovni prepis v teku]';

/** One uninterrupted turn of a single speaker (consecutive segments merged). */
export interface SpeakerTurn {
  speaker: string | null;
  /** Display name, '' when the segment has no speaker. */
  name: string;
  startMs: number;
  text: string;
}

/** Transcript of a clip, plus an optional muted status note (e.g. RETRANSCRIBING_LABEL). */
type ClipContent =
  | { kind: 'turns'; turns: SpeakerTurn[]; note?: string }
  | { kind: 'paragraphs'; paragraphs: string[]; note?: string };

export type ClipBody = { kind: 'pending' } | { kind: 'error' } | { kind: 'empty' } | ClipContent;

/** Clips in document order. The API already sorts by seq; sorting a copy keeps exports correct for cached/optimistic data too. */
export function sortedClips(doc: Pick<DocumentFull, 'clips'>): Clip[] {
  return [...doc.clips].sort((a, b) => a.seq - b.seq || a.createdAt.localeCompare(b.createdAt));
}

/** Non-empty, trimmed lines of a free-text field. */
export function textParagraphs(text: string | null | undefined): string[] {
  if (!text) return [];
  return text
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/** Merge consecutive segments of the same speaker into turns; segments without text are dropped. */
export function speakerTurns(doc: Pick<DocumentFull, 'speakers'>, clip: Clip): SpeakerTurn[] {
  // Stable sort: segments with equal start keep the provider's order.
  const segments = [...clip.segments].sort((a, b) => a.startMs - b.startMs);
  const turns: SpeakerTurn[] = [];
  for (const segment of segments) {
    const text = segment.text.replace(/\s+/g, ' ').trim();
    if (!text) continue;
    const last = turns[turns.length - 1];
    if (last && last.speaker === segment.speaker) {
      last.text = `${last.text} ${text}`;
    } else {
      turns.push({
        speaker: segment.speaker,
        name: speakerName(doc, segment.speaker),
        startMs: segment.startMs,
        text,
      });
    }
  }
  return turns;
}

/** The transcript a clip currently has (whatever its status), null when it has none. */
function clipContent(doc: Pick<DocumentFull, 'speakers'>, clip: Clip, opts: { turns: boolean }): ClipContent | null {
  if (opts.turns && clip.diarized && clip.segments.length > 0) {
    const turns = speakerTurns(doc, clip);
    if (turns.length > 0) return { kind: 'turns', turns };
  }
  const paragraphs = textParagraphs(clip.text);
  return paragraphs.length > 0 ? { kind: 'paragraphs', paragraphs } : null;
}

/**
 * What to print for a clip. Diarized meeting clips are rendered as speaker turns; everything else
 * (and diarized clips that somehow have no segments) as plain paragraphs of `clip.text`.
 *
 * Like the pages, the export prints a clip's text whenever it has one: a re-transcribed clip keeps
 * its previous (possibly hand-corrected) text while it is queued and when the new attempt fails.
 * While it is being transcribed again the text is marked, since it may still be replaced. The
 * pending/error placeholders are only used for clips that have no text at all.
 */
export function clipBody(doc: Pick<DocumentFull, 'speakers'>, clip: Clip, opts: { turns: boolean }): ClipBody {
  const pending = clip.status === 'queued' || clip.status === 'processing';
  const content = clipContent(doc, clip, opts);
  if (content) return pending ? { ...content, note: RETRANSCRIBING_LABEL } : content;
  if (pending) return { kind: 'pending' };
  if (clip.status === 'error') return { kind: 'error' };
  return { kind: 'empty' };
}

/** Label for a clip that has no transcript to show, null when it has one. */
export function placeholderFor(body: ClipBody): string | null {
  switch (body.kind) {
    case 'pending':
      return PENDING_LABEL;
    case 'error':
      return ERROR_LABEL;
    case 'empty':
      return EMPTY_LABEL;
    default:
      return null;
  }
}

/**
 * Names of the speakers that actually occur in the exported speaker turns, in order of first
 * appearance. Like clipBody, this includes clips that are being transcribed again (they keep their
 * previous segments).
 */
export function participantNames(doc: Pick<DocumentFull, 'speakers' | 'clips'>): string[] {
  const names: string[] = [];
  const seenKeys = new Set<string>();
  for (const clip of sortedClips(doc)) {
    if (!clip.diarized) continue;
    for (const turn of speakerTurns(doc, clip)) {
      if (!turn.speaker || seenKeys.has(turn.speaker)) continue;
      seenKeys.add(turn.speaker);
      // Two keys renamed to the same person (diarization split one voice) are one participant.
      if (turn.name && !names.includes(turn.name)) names.push(turn.name);
    }
  }
  return names;
}

/** "Datum: sobota, 24. oktober 2026 · Trajanje: 12:05 · Projekt: BP20" (parts omitted when there is nothing). */
export function meetingDateLine(doc: Pick<DocumentFull, 'createdAt' | 'durationMs' | 'projectName'>): string {
  const parts = [`Datum: ${formatLongDate(doc.createdAt)}`];
  if (doc.durationMs > 0) parts.push(`Trajanje: ${formatDuration(doc.durationMs)}`);
  if (doc.projectName) parts.push(`Projekt: ${doc.projectName}`);
  return parts.join(' · ');
}

/** "Del 2 (14:05)" – heading for one recording part of a meeting with several clips. */
export function partHeading(index: number, clip: Clip): string {
  const time = formatTime(clip.createdAt);
  return time ? `Del ${index + 1} (${time})` : `Del ${index + 1}`;
}

/** Inspection header fields in report order (Datum is added separately and is always present). */
export const INSPECTION_FIELDS: ReadonlyArray<readonly [Exclude<keyof InspectionMeta, 'opombe'>, string]> = [
  ['objekt', 'Objekt'],
  ['lokacija', 'Lokacija'],
  ['investitor', 'Investitor'],
  ['izvajalec', 'Izvajalec'],
  ['nadzornik', 'Nadzornik'],
  ['prisotni', 'Prisotni'],
];

export interface HeaderRow {
  label: string;
  /** Lines of the value (free-text fields may contain line breaks, e.g. a list of attendees). */
  lines: string[];
}

/** Header rows of an inspection report; empty optional fields are skipped. */
export function inspectionHeaderRows(doc: Pick<DocumentFull, 'createdAt' | 'meta' | 'projectName'>): HeaderRow[] {
  const rows: HeaderRow[] = [{ label: 'Datum', lines: [formatLongDate(doc.createdAt)] }];
  if (doc.projectName) rows.push({ label: 'Projekt', lines: [doc.projectName] });
  for (const [key, label] of INSPECTION_FIELDS) {
    const lines = textParagraphs(doc.meta[key]);
    if (lines.length > 0) rows.push({ label, lines });
  }
  return rows;
}

/** One numbered finding of an inspection report. */
export interface InspectionEntry {
  number: number;
  /** "10:02" in Ljubljana time, '' when unknown. */
  time: string;
  body: ClipBody;
}

export function inspectionEntries(doc: Pick<DocumentFull, 'speakers' | 'clips'>): InspectionEntry[] {
  return sortedClips(doc).map((clip, index) => ({
    number: index + 1,
    time: formatTime(clip.createdAt),
    body: clipBody(doc, clip, { turns: false }),
  }));
}

/** Full text of a reminder (all clips; normally exactly one). Falls back to the title while nothing is transcribed. */
export function noteText(doc: Pick<DocumentFull, 'title' | 'speakers' | 'clips'>): string {
  const parts: string[] = [];
  let hasText = false;
  for (const clip of sortedClips(doc)) {
    const body = clipBody(doc, clip, { turns: false });
    if (body.kind === 'paragraphs') {
      parts.push(body.paragraphs.join('\n'));
      hasText = true;
    } else if (body.kind !== 'empty') {
      parts.push(placeholderFor(body) ?? '');
    }
  }
  if (!hasText) {
    // A typed or auto-derived title is more useful than a bare "[prepis v teku]".
    const title = doc.title.trim();
    if (title) return parts.length > 0 ? `${title}\n${parts.join('\n')}` : title;
  }
  return parts.join('\n\n');
}
